import React, {useEffect, useState} from 'react';
import {AlertCircle, ArrowLeft, BookOpen, Check, ChevronLeft, ChevronRight, Eye, Loader2, X} from 'lucide-react';

export interface Word {
    id: number;
    kanji: string;
    hiragana: string;
    meaning: string;
    isMemorized?: boolean | null;
    memorized?: boolean | null;
}

interface LearningScreenProps {
    level: string;
    chapter: number;
    words: Word[];
    currentIndex: number;
    loading: boolean;
    theme: { color: string; bg: string; text: string; darkBg: string; darkText: string };
    onBack: () => void;
    onNext: () => void;
    onPrev: () => void;
    onUpdateProgress: (wordId: number, isMemorized: boolean) => Promise<boolean>;
}

const LearningScreen = ({
                            level,
                            chapter,
                            words,
                            currentIndex,
                            loading,
                            theme,
                            onBack,
                            onNext,
                            onPrev,
                            onUpdateProgress
                        }: LearningScreenProps) => {
    const [showHiragana, setShowHiragana] = useState(false);
    const [showMeaning, setShowMeaning] = useState(false);

    useEffect(() => {
        setShowHiragana(false);
        setShowMeaning(false);
    }, [currentIndex]);

    const currentWord = words[currentIndex];
    const memorizedStatus = currentWord ? (currentWord.isMemorized ?? currentWord.memorized ?? null) : null;
    const isKnown = memorizedStatus === true;
    const isUnknown = memorizedStatus === false;
    const progress = words.length > 0 ? Math.round(((currentIndex + 1) / words.length) * 100) : 0;

    const handleChoice = async (status: boolean) => {
        if (!currentWord) return;

        const isSaved = await onUpdateProgress(currentWord.id, status);
        if (!isSaved) return;

        setShowHiragana(true);
        setShowMeaning(true);

        if (currentIndex < words.length - 1) {
            setTimeout(() => {
                onNext();
            }, 1000);
        }
    };

    if (loading) {
        return (
            <div className="h-96 flex flex-col items-center justify-center bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4"/>
                <p className="text-gray-400 dark:text-gray-500">단어장을 불러오는 중...</p>
            </div>
        );
    }

    if (!currentWord) {
        return (
            <div className="h-96 flex flex-col items-center justify-center bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 text-center p-8">
                <p className="text-xl text-gray-600 dark:text-gray-300 font-medium mb-4">학습할 단어가 없습니다.</p>
                <button
                    onClick={onBack}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    다른 챕터 선택하기
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto py-6 px-4 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={onBack}
                    className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 mr-1"/>
                    <span className="text-sm font-medium">목록으로</span>
                </button>
                <div className="text-center">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${theme.bg} ${theme.darkBg} ${theme.text} ${theme.darkText} mr-2`}>
                        {level}
                    </span>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Chapter {chapter}
                    </span>
                </div>
                <div className="w-20"/>
            </div>

            <div className="space-y-6">
                <div className="flex items-center gap-3 px-1">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                            className={`h-full ${theme.color} transition-all duration-300`}
                            style={{width: `${progress}%`}}
                        />
                    </div>
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 w-12 text-right">
                        {currentIndex + 1} / {words.length}
                    </span>
                </div>

                <div className={`bg-white dark:bg-gray-800 rounded-3xl shadow-lg border-2 overflow-hidden relative min-h-[400px] flex flex-col transition-colors duration-300 ${
                    isKnown
                        ? 'border-green-200 dark:border-green-900/50 shadow-green-50 dark:shadow-green-900/10'
                        : isUnknown
                            ? 'border-red-200 dark:border-red-900/50 shadow-red-50 dark:shadow-red-900/10'
                            : 'border-gray-100 dark:border-gray-700'
                }`}>
                    {isKnown ? (
                        <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-fade-in">
                            <Check className="w-3 h-3"/>
                            암기 완료
                        </div>
                    ) : isUnknown ? (
                        <div className="absolute top-4 right-4 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-fade-in">
                            <X className="w-3 h-3"/>
                            아직 몰라요
                        </div>
                    ) : <div className="absolute top-4 right-4 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 animate-fade-in">
                        <AlertCircle className="w-3 h-3" />
                        학습 중
                    </div>}

                    <div
                        className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-8 cursor-pointer"
                        onClick={() => {
                            setShowHiragana(true);
                            setShowMeaning(true);
                        }}
                    >
                        <div className="space-y-2">
                            <h1 className="text-6xl md:text-7xl font-black text-gray-800 dark:text-gray-100 tracking-wide mb-4">
                                {currentWord.kanji}
                            </h1>
                        </div>

                        <div className="w-full space-y-3 max-w-xs" onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => setShowHiragana(!showHiragana)}
                                className={`w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 ${
                                    showHiragana
                                        ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50'
                                        : 'bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                {showHiragana ? (
                                    <span className="text-xl font-bold">{currentWord.hiragana}</span>
                                ) : (
                                    <>
                                        <Eye className="w-4 h-4"/>
                                        <span className="text-sm font-medium">히라가나 보기</span>
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => setShowMeaning(!showMeaning)}
                                className={`w-full py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 ${
                                    showMeaning
                                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900/50'
                                        : 'bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                            >
                                {showMeaning ? (
                                    <span className="text-lg font-medium">{currentWord.meaning}</span>
                                ) : (
                                    <>
                                        <BookOpen className="w-4 h-4"/>
                                        <span className="text-sm font-medium">뜻 보기</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 flex justify-between items-center border-t border-gray-100 dark:border-gray-700">
                        <button
                            onClick={() => handleChoice(false)}
                            className={`flex flex-col items-center gap-1 transition-colors p-2 ${
                                isUnknown
                                    ? 'text-red-600 scale-110'
                                    : 'text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400'
                            }`}
                        >
                            <div className={`w-10 h-10 rounded-full border flex items-center justify-center shadow-sm transition-colors ${
                                isUnknown
                                    ? 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400'
                                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 hover:text-red-500'
                            }`}>
                                <X className="w-5 h-5"/>
                            </div>
                            <span className="text-[10px] font-medium">몰라요</span>
                        </button>

                        <div className="flex gap-4">
                            <button
                                onClick={onPrev}
                                disabled={currentIndex === 0}
                                className="w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            >
                                <ChevronLeft className="w-6 h-6"/>
                            </button>
                            <button
                                onClick={onNext}
                                disabled={currentIndex === words.length - 1}
                                className={`w-12 h-12 flex items-center justify-center rounded-full text-white shadow-md transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${theme.color} hover:brightness-110`}
                            >
                                <ChevronRight className="w-6 h-6"/>
                            </button>
                        </div>

                        <button
                            onClick={() => handleChoice(true)}
                            className={`flex flex-col items-center gap-1 transition-colors p-2 ${
                                isKnown
                                    ? 'text-green-600 scale-110'
                                    : 'text-gray-400 dark:text-gray-500 hover:text-green-500 dark:hover:text-green-400'
                            }`}
                        >
                            <div className={`w-10 h-10 rounded-full border flex items-center justify-center shadow-sm transition-colors ${
                                isKnown
                                    ? 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-900/50 text-green-600 dark:text-green-400'
                                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-200 hover:text-green-600'
                            }`}>
                                <Check className="w-5 h-5"/>
                            </div>
                            <span className="text-[10px] font-medium">알아요</span>
                        </button>
                    </div>
                </div>

                <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                    상태 버튼을 누르면 자동으로 다음 카드로 넘어갑니다.
                </p>
            </div>
        </div>
    );
};

export default LearningScreen;
